import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useFacilityAvailability } from '../../hooks/useFacilityAvailability';
import { FACILITIES, getAllFacilityIds, getOperatingHours } from '../../hooks/facilityConfig';
import './TodayAvailabilityGrid.css';

// Generate time slots from 5:00 to 23:00 in 30-minute increments
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 5; hour <= 23; hour++) {
    slots.push(`${hour.toString().padStart(2, '0')}:00`);
    if (hour < 23) {
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
  }
  return slots;
};

const TodayAvailabilityGrid = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [hoveredSlot, setHoveredSlot] = useState(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState(null);
  const [viewingTomorrow, setViewingTomorrow] = useState(false);
  const gridRef = useRef(null);
  const timeSlots = generateTimeSlots();
  
  // Use the hook to fetch facility availability for 2 days (today and tomorrow)
  const { data, loading, error, refetch } = useFacilityAvailability({
    facilityIds: getAllFacilityIds(),
    days: 2, // Fetch 2 days to support tomorrow view
    autoRefetch: true,
    refetchInterval: 5 * 60 * 1000 // 5 minutes
  });
  
  // Convert FACILITIES object to array for rendering
  const facilitiesArray = Object.values(FACILITIES);

  // Update current time every 30 seconds for smoother updates
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 30000); // Update every 30 seconds
    return () => clearInterval(timer);
  }, []);

  // Handle window resize to update time indicator position
  useEffect(() => {
    const handleResize = () => {
      // Force re-render to update time indicator position
      setCurrentTime(new Date());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get date in YYYY/MM/DD format
  const getDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  };
  
  // Get today's date
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const targetDate = viewingTomorrow ? tomorrow : today;

  // Get slot status for a specific facility and time
  const getSlotStatus = (facility, time) => {
    if (!data || !data.facilities || !data.facilities[facility.id]) return 'unknown';
    
    const facilityData = data.facilities[facility.id];
    if (facilityData.error || !facilityData.days || facilityData.days.length === 0) return 'unknown';
    
    // Find target date's data (today or tomorrow)
    const dayIndex = viewingTomorrow ? 1 : 0;
    const targetData = facilityData.days[dayIndex];
    if (!targetData || !targetData.slots) return 'unknown';

    // Check if facility is closed at this time
    const [hour, minute] = time.split(':').map(Number);
    const [openHour, openMinute] = facility.openTime.split(':').map(Number);
    const [closeHour, closeMinute] = facility.closeTime.split(':').map(Number);
    
    const timeMinutes = hour * 60 + minute;
    const openMinutes = openHour * 60 + openMinute;
    let closeMinutes = closeHour * 60 + closeMinute;
    
    // Handle overnight facilities
    if (facility.type === 'daily' && closeHour < openHour) {
      closeMinutes += 24 * 60;
    }

    if (timeMinutes < openMinutes || (facility.type === 'hourly' && timeMinutes >= closeMinutes)) {
      return 'closed';
    }

    // Find the slot status
    const slot = targetData.slots.find(s => s.time === time);
    if (!slot) return 'unknown';

    // First check the status field from API
    if (slot.status === 'booked') return 'booked';
    if (slot.status === 'past-deadline') return 'past-deadline';

    // Then check if slot is available
    if (!slot.available) return 'booked';

    // Check if time is in the past (only for today view) - AFTER checking booking status
    // This allows us to see past bookings throughout the day
    if (!viewingTomorrow) {
      const now = currentTime;
      const slotTime = new Date();
      slotTime.setHours(hour, minute, 0, 0);

      if (slotTime < now) {
        return 'past-available'; // Was available but time has passed
      }
    }
    
    // For dining and party rooms, check if we're within 3 hours of the slot (only for today)
    if (!viewingTomorrow && (facility.id === 'executive-dining' || facility.id === 'party-room')) {
      const deadlineHours = facility.bookingDeadlineHours || 3;
      const deadlineMs = deadlineHours * 60 * 60 * 1000;
      const slotTime = new Date();
      slotTime.setHours(hour, minute, 0, 0);
      const bookingDeadline = new Date(slotTime.getTime() - deadlineMs);
      
      if (currentTime > bookingDeadline) {
        return 'past-deadline';
      }
    }
    
    // Otherwise, it's available
    return 'available';
  };

  // Get cell class based on status
  const getCellClass = (status) => {
    switch (status) {
      case 'available':
        return 'slot-available';
      case 'booked':
        return 'slot-booked';
      case 'past-available':
        return 'slot-past-available'; // New class for past but was available
      case 'past':
      case 'past-deadline':
        return 'slot-past';
      case 'closed':
        return 'slot-closed';
      default:
        return 'slot-unknown';
    }
  };

  // Get cell content based on status with enhanced indicators
  const getCellContent = (status) => {
    switch (status) {
      case 'available':
        return '◯';
      case 'booked':
        return '✕';
      case 'past-available':
        return '◯'; // Show as available (will be styled dimmed in CSS)
      case 'past':
      case 'closed':
        return '―';
      case 'past-deadline':
        return '/'; // Slash for past-deadline
      default:
        return '?';
    }
  };

  // Get availability percentage for progress bars
  const getAvailabilityPercentage = (facility, time) => {
    const status = getSlotStatus(facility, time);
    if (status === 'available') return 100;
    if (status === 'past-deadline') return 50;
    return 0;
  };

  // Time navigation functions
  const scrollToTimeRange = useCallback((range) => {
    if (!gridRef.current) return;
    
    let targetHour;
    switch (range) {
      case 'morning':
        targetHour = 8;
        break;
      case 'afternoon':
        targetHour = 14;
        break;
      case 'evening':
        targetHour = 18;
        break;
      case 'current':
        targetHour = currentTime.getHours();
        break;
      default:
        return;
    }
    
    if (targetHour >= 5 && targetHour <= 23) {
      const scrollPosition = ((targetHour - 5) / 18) * gridRef.current.scrollWidth;
      gridRef.current.scrollTo({ left: scrollPosition - 100, behavior: 'smooth' });
    }
    
    setSelectedTimeRange(range);
  }, [currentTime]);

  // Get booking time range for a specific slot
  const getBookingTimeRange = (facility, time) => {
    if (!data || !data.facilities || !data.facilities[facility.id]) return null;
    
    const facilityData = data.facilities[facility.id];
    if (facilityData.error || !facilityData.days || facilityData.days.length === 0) return null;
    
    // Find target date's data
    const dayIndex = viewingTomorrow ? 1 : 0;
    const targetData = facilityData.days[dayIndex];
    if (!targetData || !targetData.slots) return null;

    // Find the current slot
    const currentSlot = targetData.slots.find(s => s.time === time);
    if (!currentSlot || currentSlot.available) return null;

    // Find the start and end of this booking block
    const slots = targetData.slots;
    const currentIndex = slots.findIndex(s => s.time === time);
    
    let startIndex = currentIndex;
    let endIndex = currentIndex;
    
    // Find start of booking block (go backwards)
    while (startIndex > 0 && !slots[startIndex - 1].available) {
      startIndex--;
    }
    
    // Find end of booking block (go forwards)
    while (endIndex < slots.length - 1 && !slots[endIndex + 1].available) {
      endIndex++;
    }
    
    return {
      startTime: slots[startIndex].time,
      endTime: slots[endIndex].time
    };
  };

  // Get detailed slot information for hover tooltip
  const getSlotDetails = (facility, time, status) => {
    const details = {
      facilityName: facility.name,
      time: time,
      status: status,
      statusText: '',
      bookingRange: null
    };

    // For booked slots, get booking time range
    if (status === 'booked') {
      const bookingRange = getBookingTimeRange(facility, time);
      if (bookingRange) {
        details.bookingRange = bookingRange;
        details.statusText = `予約済み\n${bookingRange.startTime} - ${bookingRange.endTime}`;
      } else {
        details.statusText = '予約済み（利用不可）';
      }
    } else {
      // Get status text for non-booked slots
      switch (status) {
        case 'past-available':
          details.statusText = '利用可能でした（時間経過）';
          break;
        case 'past-deadline':
          if (facility.bookingDeadlineHours) {
            details.statusText = `予約締切済み（${facility.bookingDeadlineHours}時間前締切）\n直接お越しいただければ利用可能です`;
          } else {
            details.statusText = '予約締切済み（直接利用は可能）';
          }
          break;
        case 'closed':
          details.statusText = '営業時間外';
          break;
        case 'past':
          details.statusText = '時間が経過しています';
          break;
        default:
          details.statusText = '状態不明';
      }
    }

    // Add operating hours info for non-available slots
    if (status !== 'available') {
      const hours = getOperatingHours(facility.id);
      if (hours && facility.type === 'hourly') {
        details.operatingHours = `営業時間: ${hours.display}`;
      }
    }

    return details;
  };

  // Format current time marker position
  const getCurrentTimePosition = () => {
    const now = currentTime;
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    // Check if current time is outside operating hours
    if (currentHour < 5 || currentHour >= 23) return null;
    
    // Calculate position in pixels
    // Get responsive slot width based on screen size
    const getSlotWidth = () => {
      const width = window.innerWidth;
      if (width <= 768) return 35; // Mobile
      return 40; // Desktop
    };
    
    const minutesSince5AM = (currentHour - 5) * 60 + currentMinute;
    const slotWidth = getSlotWidth(); // Responsive slot width
    const minutesPerSlot = 30;
    
    // Calculate pixel position
    const pixelPosition = (minutesSince5AM / minutesPerSlot) * slotWidth;
    
    return pixelPosition;
  };

  // Auto-scroll to current time on load (only for today view)
  useEffect(() => {
    if (!loading && gridRef.current && !selectedTimeRange && !viewingTomorrow) {
      const timer = setTimeout(() => {
        scrollToTimeRange('current');
      }, 500); // Delay to ensure grid is rendered
      
      return () => clearTimeout(timer);
    }
  }, [loading, currentTime, selectedTimeRange, scrollToTimeRange, viewingTomorrow]);

  if (loading) {
    return (
      <div className="availability-grid-container">
        <h2>本日の施設利用状況</h2>
        <div className="loading-message">読み込み中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="availability-grid-container">
        <h2>本日の施設利用状況</h2>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  const currentTimePosition = getCurrentTimePosition();

  return (
    <div className="availability-grid-container">
      <div className="grid-header-with-toggle">
        <h2>{viewingTomorrow ? '明日' : '本日'}の施設利用状況</h2>
        <div className="date-toggle-buttons">
          <button 
            className={`date-toggle-btn ${!viewingTomorrow ? 'active' : ''}`}
            onClick={() => setViewingTomorrow(false)}
            aria-label="今日の状況を表示"
          >
            今日
          </button>
          <button 
            className={`date-toggle-btn ${viewingTomorrow ? 'active' : ''}`}
            onClick={() => setViewingTomorrow(true)}
            aria-label="明日の状況を表示"
          >
            明日
          </button>
        </div>
      </div>
      
      {/* Time Navigation Controls */}
      <div className="time-navigation">
        <div className="time-nav-buttons">
          <button 
            className={`time-nav-btn ${selectedTimeRange === 'morning' ? 'active' : ''}`}
            onClick={() => scrollToTimeRange('morning')}
            aria-label="朝の時間帯へジャンプ"
          >
            <span className="nav-icon">🌅</span>
            <span className="nav-label">朝</span>
            <span className="nav-time">05:00-12:00</span>
          </button>
          <button 
            className={`time-nav-btn ${selectedTimeRange === 'afternoon' ? 'active' : ''}`}
            onClick={() => scrollToTimeRange('afternoon')}
            aria-label="昼の時間帯へジャンプ"
          >
            <span className="nav-icon">☀️</span>
            <span className="nav-label">昼</span>
            <span className="nav-time">12:00-17:00</span>
          </button>
          <button 
            className={`time-nav-btn ${selectedTimeRange === 'evening' ? 'active' : ''}`}
            onClick={() => scrollToTimeRange('evening')}
            aria-label="夜の時間帯へジャンプ"
          >
            <span className="nav-icon">🌙</span>
            <span className="nav-label">夜</span>
            <span className="nav-time">17:00-23:00</span>
          </button>
          {!viewingTomorrow && (
            <button 
              className={`time-nav-btn current-time-btn ${selectedTimeRange === 'current' ? 'active' : ''}`}
              onClick={() => scrollToTimeRange('current')}
              aria-label="現在時刻へジャンプ"
            >
              <span className="nav-icon">⏰</span>
              <span className="nav-label">現在</span>
              <span className="nav-time">{currentTime.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}</span>
            </button>
          )}
        </div>
        <div className="time-range-info">
          <span className="current-time-display">
            {viewingTomorrow ? 
              `表示日: ${targetDate.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}日` :
              `現在時刻: ${currentTime.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}`
            }
          </span>
        </div>
      </div>
      
      <div className="grid-wrapper">
        <div className="facility-labels">
          <div className="time-label-placeholder"></div>
          {facilitiesArray.map(facility => (
            <div key={facility.id} className="facility-label">
              <span className="facility-icon">{facility.icon}</span>
              <span className="facility-name">{facility.shortName}</span>
            </div>
          ))}
        </div>
        
        <div className="grid-scroll-container" ref={gridRef}>
          <div className="time-header">
            {timeSlots.map(time => (
              <div key={time} className="time-slot-header">
                {time}
              </div>
            ))}
          </div>
          
          <div className="availability-grid" style={{ position: 'relative' }}>
            {!viewingTomorrow && currentTimePosition !== null && (
              <div 
                className="current-time-indicator"
                style={{ left: `${currentTimePosition}px` }}
                data-current-time={currentTime.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}
                aria-label={`現在時刻: ${currentTime.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}`}
              />
            )}
            
            {facilitiesArray.map(facility => (
              <div key={facility.id} className="facility-row">
                {facility.type === 'daily' ? (
                  // Special handling for guest rooms - show throughout the day
                  timeSlots.map(time => {
                    const [hour, minute] = time.split(':').map(Number);
                    const checkInHour = parseInt(facility.checkInTime?.split(':')[0] || '15');
                    const checkOutHour = parseInt(facility.checkOutTime?.split(':')[0] || '10');
                    
                    // Check if target day's room is available
                    const dayIndex = viewingTomorrow ? 1 : 0;
                    const targetDayAvailable = data && data.facilities && data.facilities[facility.id] && 
                                         data.facilities[facility.id].days && data.facilities[facility.id].days[dayIndex] &&
                                         data.facilities[facility.id].days[dayIndex].available;
                    
                    let status = 'closed';
                    let content = '';
                    
                    // Before checkout time (10:00) - show previous night's guest
                    if (hour < checkOutHour) {
                      status = 'booked';
                      content = '×';
                    }
                    // Between checkout and check-in (cleaning time)
                    else if (hour < checkInHour) {
                      status = 'closed';
                      content = '−';
                    }
                    // Check-in time indicator
                    else if (hour === checkInHour && minute === 0) {
                      status = targetDayAvailable ? 'check-in-available' : 'check-in-booked';
                      content = targetDayAvailable ? 'IN' : '×';
                    }
                    // After check-in time
                    else if (hour >= checkInHour) {
                      status = targetDayAvailable ? 'available' : 'booked';
                      content = targetDayAvailable ? '◯' : '×';
                    }
                    
                    // Get tooltip details for guest rooms
                    const slotDetails = {
                      facilityName: facility.name,
                      time: time,
                      status: status,
                      statusText: status === 'booked' ? (hour < checkOutHour ? `宿泊中\n15:00 - 翌10:00` : '予約済み') : 
                                 status === 'available' ? '利用可能' : 
                                 status === 'closed' ? '清掃中' : '状態不明'
                    };
                    
                    const isHovered = hoveredSlot && 
                                    hoveredSlot.facilityId === facility.id && 
                                    hoveredSlot.time === time;
                    
                    return (
                      <div
                        key={`${facility.id}-${time}`}
                        className={`time-slot guest-room-slot ${getCellClass(status)} ${isHovered ? 'slot-hovered' : ''}`}
                        role="cell"
                        aria-label={`${facility.name} ${time} ${slotDetails.statusText}`}
                        tabIndex={0}
                        onMouseEnter={() => setHoveredSlot({ facilityId: facility.id, time, details: slotDetails })}
                        onMouseLeave={() => setHoveredSlot(null)}
                      >
                        <div className="slot-content">
                          <span className="slot-indicator">
                            {content}
                          </span>
                        </div>
                        {isHovered && (
                          <div className="slot-tooltip enhanced-tooltip">
                            <div className="tooltip-header">
                              <span className="tooltip-facility">{slotDetails.facilityName}</span>
                              <span className="tooltip-time">{slotDetails.time}</span>
                            </div>
                            <div className="tooltip-body">
                              <div className="tooltip-status">{slotDetails.statusText}</div>
                              <div className="tooltip-actions">
                                {status === 'available' && (
                                  <small className="tooltip-hint">予約可能です</small>
                                )}
                                {status === 'booked' && hour >= checkInHour && (
                                  <small className="tooltip-hint">利用不可</small>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  // Regular hourly slots
                  timeSlots.map(time => {
                    const status = getSlotStatus(facility, time);
                    const slotDetails = getSlotDetails(facility, time, status);
                    const isHovered = hoveredSlot && 
                                    hoveredSlot.facilityId === facility.id && 
                                    hoveredSlot.time === time;
                    
                    return (
                      <div
                        key={`${facility.id}-${time}`}
                        className={`time-slot ${getCellClass(status)} ${isHovered ? 'slot-hovered' : ''}`}
                        role="cell"
                        aria-label={`${facility.shortName} ${time} ${slotDetails.statusText}`}
                        tabIndex={0}
                        onMouseEnter={() => setHoveredSlot({ facilityId: facility.id, time, details: slotDetails })}
                        onMouseLeave={() => setHoveredSlot(null)}
                      >
                        <div className="slot-content">
                          <span className="slot-indicator">
                            {getCellContent(status)}
                          </span>
                          {/* Progress bar for availability */}
                          {(status === 'available' || status === 'past-deadline') && (
                            <div className="availability-progress">
                              <div 
                                className="progress-bar"
                                style={{ width: `${getAvailabilityPercentage(facility, time)}%` }}
                              />
                            </div>
                          )}
                        </div>
                        {isHovered && (
                          <div className="slot-tooltip enhanced-tooltip">
                            <div className="tooltip-header">
                              <span className="tooltip-facility">{slotDetails.facilityName}</span>
                              <span className="tooltip-time">{slotDetails.time}</span>
                            </div>
                            <div className="tooltip-body">
                              {status !== 'available' && (
                                <div className="tooltip-status">{slotDetails.statusText}</div>
                              )}
                              {slotDetails.operatingHours && status !== 'available' && (
                                <div className="tooltip-hours">{slotDetails.operatingHours}</div>
                              )}
                              <div className="tooltip-actions">
                                {status === 'booked' && slotDetails.bookingRange && (
                                  <small className="tooltip-hint">予約時間: {slotDetails.bookingRange.startTime} - {slotDetails.bookingRange.endTime}</small>
                                )}
                                {status === 'past-deadline' && (
                                  <small className="tooltip-hint">直接お越しください</small>
                                )}
                                {status === 'available' && (
                                  <small className="tooltip-hint">利用可能</small>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="grid-legend">
        <div className="legend-item">
          <span className="legend-indicator available">◯</span>
          <span>利用可能</span>
        </div>
        <div className="legend-item">
          <span className="legend-indicator booked">✕</span>
          <span>予約済み</span>
        </div>
        <div className="legend-item">
          <span className="legend-indicator past-deadline">/</span>
          <span>予約締切（利用可）</span>
        </div>
        <div className="legend-item">
          <span className="legend-indicator closed">―</span>
          <span>営業時間外</span>
        </div>
        <div className="legend-item">
          <div className="current-time-indicator-sample"></div>
          <span>現在時刻</span>
        </div>
        <div className="legend-item">
          <span className="legend-indicator check-in">IN</span>
          <span>チェックイン時間</span>
        </div>
      </div>
      
      <div className="facility-hours-info">
        <h3>施設営業時間</h3>
        <div className="hours-grid">
          {facilitiesArray.map(facility => {
            const hours = getOperatingHours(facility.id);
            return (
              <div key={facility.id} className="facility-hours-item">
                <span className="facility-hours-icon">{facility.icon}</span>
                <span className="facility-hours-name">{facility.shortName}</span>
                <span className="facility-hours-time">{hours ? hours.display : 'N/A'}</span>
              </div>
            );
          })}
        </div>
      </div>
      
      <button 
        className="refresh-button"
        onClick={refetch}
        aria-label="データを更新"
        disabled={loading}
      >
        🔄 更新
      </button>
    </div>
  );
};

export default TodayAvailabilityGrid;